import { useEffect, useState, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  Plus, Book, Edit, Trash2, LayoutDashboard, Table as TableIcon,
  Loader, Search, Layers3, BookKey, ImagePlus,
  BookOpen, ClipboardList, FileText
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import Drawer from '@/layout/Drawer'
import ConfirmModal from '@/widgets/ConfirmModal'
import { getSubjects, addSubject, updateSubject, deleteSubject } from '@/api/kuizy'
import { formatDateTime } from '@/settings/master'
import ViewSwitcher from '../components/ui/ViewSwitcher'

export default function ContentPage() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)

  const [view, setView] = useState('card')
  const [searchInput, setSI] = useState('')
  const [searchText, setST] = useState('')

  const [open, setOpen] = useState(false)
  const [isNew, setNew] = useState(false)
  const [saving, setSav] = useState(false)
  const [item, setItem] = useState(null)

  const [del, setDel] = useState(null)
  const [delLoad, setDelLoad] = useState(false)
  const [alert, setAlert] = useState({ open: false, msg: '' })

  const [confirmSave, setConfirmSave] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await getSubjects()
      setRows(Array.isArray(data) ? data : [])
    } catch { setAlert({ open: true, msg: 'โหลดเนื้อหาไม่สำเร็จ' }) }
    finally { setLoading(false) }
  }, [])
  useEffect(() => { fetchData() }, [fetchData])

  const list = useMemo(() =>
    rows.filter(r =>
      !searchText ||
      (r.name || '').toLowerCase().includes(searchText.toLowerCase()) ||
      (r.code || '').toLowerCase().includes(searchText.toLowerCase())
    ), [rows, searchText])

  const startEdit = c => { setItem({ ...c }); setNew(false); setOpen(true) }
  const startNew = () => { setItem({ code: '', name: '', image_preview: '', image_url: '', image_file: null, }); setNew(true); setOpen(true) }

  const openConfirmSave = () => {
    if (!item || !item.name?.trim() || !item.code?.trim()) {
      setAlert({ open: true, msg: 'กรอกข้อมูลให้ครบ' }); return
    }
    setConfirmSave(true)
  }

  const doSave = async () => {
    if (!item) return
    setSav(true)
    try {
      if (isNew) {
        const formData = new FormData()
        formData.append('code', item.code.trim())
        formData.append('name', item.name.trim())
        if (item.image_file) {
          formData.append('image', item.image_file)
        }

        const { data } = await addSubject(formData)
        setRows(r => [...r, data.content])
      } else {
        const formData = new FormData()
        formData.append('id', item.id)
        formData.append('code', item.code.trim())
        formData.append('name', item.name.trim())
        if (item.image_file) {
          formData.append('image', item.image_file)
        }
        await updateSubject(formData)
        setRows(r => r.map(x => x.id === item.id ? { ...x, name: item.name.trim(), code: item.code.trim() } : x))
      }
      setOpen(false)
    } catch (err) { console.log('ERR:', err?.response?.data), setAlert({ open: true, msg: 'บันทึกไม่สำเร็จ' }) }
    finally { setSav(false); setConfirmSave(false); fetchData() }
  }

  const confirmDel = async () => {
    if (!del) return
    setDelLoad(true)
    try { await deleteSubject(del.id); setRows(r => r.filter(x => x.id !== del.id)) }
    catch { setAlert({ open: true, msg: 'ลบไม่สำเร็จ' }) }
    finally { setDelLoad(false); setDel(null) }
  }

  const srcImage = (image_url) => {
    if (!image_url) return null

    const isAbsolute = /^(blob:|data:|http)/.test(image_url)
    return isAbsolute ? image_url : `${API_BASE}${image_url}`
  }

  const handleFile = e => {
    const file = e.target.files?.[0]
    if (!file) return

    const previewUrl = URL.createObjectURL(file)

    setItem(r => ({
      ...r,
      image_file: file,       // ส่งไฟล์ต้นฉบับไป backend
      image_preview: previewUrl // ใช้ preview แสดงบนจอ
    }))
  }


  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: .35 }} className="flex flex-col gap-6 p-4 sm:p-6"
    >
      <motion.header
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: .35, delay: .05 }}
        className="flex flex-wrap items-end gap-3 justify-between"
      >
        <h1 onClick={fetchData}
          className="flex items-center gap-2 text-lg font-semibold cursor-pointer">
          <Book size={20} /> จัดการเนื้อหา ({rows.length})
        </h1>

        <div className="flex flex-wrap gap-2">
          <ViewSwitcher view={view} setView={setView} />
          <button onClick={startNew}
            className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm">
            <Plus size={16} /> เพิ่มเนื้อหา
          </button>
        </div>
      </motion.header>

      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: .35, delay: .05 }}
        className="flex flex-wrap items-end gap-4 bg-white rounded-xl shadow-md p-4"
      >
        <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
          <label className="flex items-center gap-1 text-sm"><Search size={16} /> ค้นหา</label>
          <div className="relative">
            <input
              value={searchInput} onChange={e => setSI(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && setST(searchInput)}
              className="w-full border border-gray-300 rounded-lg px-10 py-2 text-sm focus:ring-2 focus:ring-primary"
              placeholder="ค้นหาชื่อเนื้อหา / ชื่อย่อเนื้อหา" />
            <Search size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          </div>
        </div>
        <button onClick={() => setST(searchInput)}
          className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm">
          <Search size={16} /> ค้นหา
        </button>
      </motion.div>

      {view === 'card' && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: .35, delay: .1 }}
          className="grid gap-6 md:gap-8 lg:gap-12 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
        >
          {loading
            ? <div className="col-span-full flex justify-center py-12"><Loader className="animate-spin" /></div>
            : list.length === 0
              ? <div className="col-span-full text-center text-gray-500 py-12">ไม่พบเนื้อหา</div>
              : list.map(c => (
                <motion.div key={c.id} whileHover={{ y: -4 }}
                  className="bg-white rounded-2xl shadow-md flex flex-col">
                  <div className="rounded-t-2xl overflow-hidden bg-neutral-100">
                    <Link to={`/content/${encodeURIComponent(c.id)}`}>
                      <img
                        src={srcImage(c.logo_url)}
                        alt={c.name}
                        className="w-full h-48"
                      />
                    </Link>
                  </div>
                  <div className='p-2'>
                    <div className="flex items-center justify-between">
                      <div>
                        <Link to={`/content/${encodeURIComponent(c.id)}`} className="text-md font-semibold text-primary leading-snug">
                          {c.name}
                        </Link>
                        <p className="text-sm mt-1 line-clamp-2">
                          {c.code}
                        </p>
                      </div>

                      <button
                        onClick={() => startEdit(c)}
                        className="text-gray-400 hover:text-primary"
                      >
                        <Edit size={18} />
                      </button>
                    </div>
                    <div className="line-clamp-2 min-h-[36px] text-[12px] mt-1 text-gray-500">
                      {c.description}
                    </div>

                    {/* stats */}
                    <div className="grid grid-cols-3 pt-2 pb-3">
                      <div className="flex items-center gap-2">
                        <div className="bg-secondary/10 p-1 rounded-md">
                          <BookOpen className="text-secondary" size={20} />
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-500">บทเรียน</p>
                          <p className="font-semibold text-[12px] text-center">{c.lesson_count || 0}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="bg-secondary/10 p-1 rounded-md">
                          <LayoutDashboard className="text-secondary" size={20} />
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-500">คลังข้อสอบ</p>
                          <p className="font-semibold text-[12px] text-center">{c.question_count || 0} ข้อ</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="bg-secondary/10 p-1 rounded-md">
                          <FileText className="text-secondary" size={20} />
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-500">ชุดข้อสอบ</p>
                          <p className="font-semibold text-[12px] text-center">{c.test_count || 0}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                </motion.div>
              ))}
        </motion.div>
      )}

      {view === 'table' && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: .35, delay: .1 }}
          className="bg-white rounded-xl shadow-md overflow-x-auto"
        >
          <table className="min-w-[780px] w-full text-sm">
            <thead className="bg-gray-100 text-gray-600">
              <tr>
                <th className="py-2 px-3 w-14 text-left">#</th>
                <th className="py-2 px-3 text-left">เนื้อหา</th>
                <th className="py-2 px-3 text-right">บทเรียน</th>
                <th className="py-2 px-3 text-right">คลังข้อสอบ</th>
                <th className="py-2 px-3 text-right">ชุดข้อสอบ</th>
                <th className="py-2 px-3 text-right hidden lg:table-cell">สร้างเมื่อ</th>
                <th className="py-2 px-3 text-right hidden lg:table-cell">แก้ไขล่าสุด</th>
                <th className="py-2 px-3 w-24 text-right">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? <tr><td colSpan={8} className="py-6 text-center"><Loader className="animate-spin" /></td></tr>
                : list.length === 0
                  ? <tr><td colSpan={8} className="py-6 text-center text-gray-500">ไม่พบเนื้อหา</td></tr>
                  : list.map((c, i) => (
                    <tr key={c.id} className="odd:bg-white even:bg-gray-50 whitespace-nowrap">
                      <td className="py-2 px-3">{i + 1}</td>
                      <td className="py-2 px-3">
                        <Link to={`/content/${encodeURIComponent(c.id)}/lessons`}
                          className="text-primary hover:underline">{c.name} ({c.code})</Link>
                      </td>
                      <td className="py-2 px-3 text-right">{c.lesson_count || 0}</td>
                      <td className="py-2 px-3 text-right">{c.question_count || 0}</td>
                      <td className="py-2 px-3 text-right">
                        <Link to={`/content/${encodeURIComponent(c.id)}/tests`} className="text-secondary hover:underline">
                          {c.test_count || 0}
                        </Link>
                      </td>
                      <td className="py-2 px-3 text-right hidden lg:table-cell">{formatDateTime(c.created_at)}</td>
                      <td className="py-2 px-3 text-right hidden lg:table-cell">{formatDateTime(c.last_updated || c.created_at)}</td>
                      <td className="py-2 px-3 text-right space-x-2">
                        <button onClick={() => startEdit(c)} className="text-primary hover:text-primary/80"><Edit size={16} /></button>
                        <button onClick={() => setDel(c)} className="text-red-600 hover:text-red-500"><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </motion.div>
      )}

      <Drawer open={open} onClose={() => setOpen(false)} title={`${isNew ? 'เพิ่ม' : 'แก้ไข'}เนื้อหา`}>
        {item && (
          <form onSubmit={e => { e.preventDefault(); openConfirmSave() }} className="flex flex-col gap-3">
            <div className="flex flex-col items-center gap-3">
              <label className="w-[370px] h-[200px] border border-gray-300 flex items-center justify-center relative overflow-hidden cursor-pointer hover:border-primary group bg-gray-50 rounded-lg">

                {item.image_preview || item.logo_url ? (
                  <img
                    src={item.image_preview || srcImage(item.logo_url)}
                    alt="logo"
                    className="object-cover w-full h-full"
                  />
                ) : (
                  <ImagePlus className="text-gray-400 group-hover:text-primary transition-colors" size={36} />
                )}

                {/* Input File */}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFile}
                />

                {/* Optional: Overlay ตอนเอาเมาส์ชี้เพื่อให้รู้ว่าเปลี่ยนรูปได้ */}
                {item.image_url || item.logo_url && (
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                    <ImagePlus className="text-white" size={24} />
                  </div>
                )}
              </label>
              <span className="text-xs text-gray-500">คลิกเพื่ออัปโหลดโลโก้</span>
            </div>
            <label className="flex items-center gap-1 text-sm">
              <Book size={14} />
              <span>
                ชื่อเนื้อหา <span className="text-red-500 font-bold">*</span>
              </span>
            </label>

            <input value={item.name}
              onChange={e => setItem({ ...item, name: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
            <label className="flex items-center gap-1 text-sm"><BookKey size={14} /> ชื่อย่อเนื้อหา <span className="text-red-500 font-bold">*</span></label>
            <input value={item.code}
              onChange={e => setItem({ ...item, code: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
            <button disabled={saving}
              className="bg-primary hover:bg-secondary text-white rounded-lg px-4 py-2 mt-2 disabled:opacity-50">
              {saving ? <Loader className="animate-spin" size={16} /> : 'บันทึก'}
            </button>
          </form>
        )}
      </Drawer>

      <AnimatePresence>
        {confirmSave &&
          <ConfirmModal
            type="confirm"
            title={isNew ? 'ยืนยันการบันทึกเนื้อหา' : 'ยืนยันการแก้ไขเนื้อหา'}
            message={`${isNew ? 'บันทึก' : 'แก้ไข'} เนื้อหา:\nชื่อ: ${item?.name || ''}\nชื่อย่อ: ${item?.code || ''}`}
            onConfirm={doSave}
            onCancel={() => setConfirmSave(false)}
            confirmDisabled={saving}
          />
        }

        {del &&
          <ConfirmModal type="confirm" title="ยืนยันการลบ"
            message={`ลบเนื้อหา "${del.name}" ?`}
            onConfirm={confirmDel} onCancel={() => setDel(null)} confirmDisabled={delLoad} />}

        {alert.open &&
          <ConfirmModal type="alert" title="แจ้งเตือน" message={alert.msg}
            onClose={() => setAlert({ open: false, msg: '' })} />}
      </AnimatePresence>
    </motion.section>
  )
}
